import { c as _c } from "react/compiler-runtime";
import { UserCircle } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import { EntityDropdownMenu, Field, FieldLayout, hasConflicts, LinkToEntity, renderBadge } from "@/src/components/cards/common";
import PatientCardEditable from "@/src/components/cards/patient-card/patient-card-editable";
import ErrorAlert from "@/src/components/error-alert";
import { Card, CardTitle, CardHeader, CardContent } from "@/src/components/shadcn/ui/card";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { useUser } from "@/src/context/user-context";
import { logger } from "@/src/lib/logging/logger";
import { canAccessInbox, canWritePatient } from "@/src/lib/rbac";
import { tryGetEhrSystemId, tryGetMrn } from "@/src/lib/resolve-ehr-ids";
import { Patient } from "@/src/models/patient";
export const PATIENT_FIELDS: Field[] = [{
  key: "familyName",
  label: "Family Name",
  comparable: true,
  required: true
}, {
  key: "givenName",
  label: "Given Name",
  comparable: true,
  required: true
}, {
  key: "middleName",
  label: "Middle Name",
  comparable: true
}, {
  key: "dateOfBirth",
  label: "Date of Birth",
  type: "date",
  comparable: true
}, {
  key: "gender",
  label: "Gender",
  comparable: true
}, {
  key: "race",
  label: "Race",
  comparable: false
}, {
  key: "ethnicity",
  label: "Ethnicity",
  comparable: false
}, {
  key: "language",
  label: "Language",
  comparable: false
}, {
  key: "ehrId",
  label: "EHR ID",
  comparable: false,
  getValue: (p: Patient) => tryGetMrn(p)?.replace(/^0+/, "") || tryGetEhrSystemId(p)
}, {
  key: "homeAddress.line1",
  label: "Address",
  comparable: true,
  colSpan: 2,
  sectionHeader: "Address"
}, {
  key: "homeAddress.line2",
  label: "Address 2",
  comparable: true
}, {
  key: "homeAddress.city",
  label: "City",
  comparable: true
}, {
  key: "homeAddress.state",
  label: "State",
  comparable: true
}, {
  key: "homeAddress.postalCode",
  label: "Zip Code",
  comparable: true
}, {
  key: "homePhoneNumber",
  label: "Home Phone",
  type: "tel",
  comparable: true
}, {
  key: "mobilePhoneNumber",
  label: "Mobile Phone",
  type: "tel",
  comparable: true
}, {
  key: "personalEmail",
  label: "Personal Email",
  comparable: true
}, {
  key: "type",
  label: "Type",
  comparable: false
}];
export default function PatientCard(t0) {
  const $ = _c(56);
  const {
    patient,
    compareMode: t1,
    secondaryPatient: t2
  } = t0;
  const compareMode = t1 === undefined ? false : t1;
  const secondaryPatient = t2 === undefined ? null : t2;
  const [isEditing, setIsEditing] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const {
    group,
    roles
  } = useUser();
  let t3;
  if ($[0] !== roles) {
    t3 = canWritePatient(roles);
    $[0] = roles;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const canWrite = t3;
  let t4;
  if ($[2] !== roles) {
    t4 = canAccessInbox(roles);
    $[2] = roles;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const canContact = t4;
  let t5;
  if ($[4] !== group) {
    t5 = f => {
      if (f.key !== "ehrId") {
        return f;
      }
      return {
        ...f,
        getValue: p => tryGetMrn(p, group)?.replace(/^0+/, "") || tryGetEhrSystemId(p, group)
      };
    };
    $[4] = group;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const fields = PATIENT_FIELDS.map(t5);
  let t6;
  let t7;
  if ($[6] !== patient || $[7] !== secondaryPatient) {
    t6 = () => {
      logger.info("Patient in patient card", {
        patient,
        secondaryPatient
      });
    };
    t7 = [patient, secondaryPatient];
    $[6] = patient;
    $[7] = secondaryPatient;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let hasAnyConflicts = false;
  if (compareMode && secondaryPatient) {
    hasAnyConflicts = hasConflicts(fields, patient, secondaryPatient);
  }
  let t8;
  if ($[10] !== isEditing) {
    t8 = () => {
      setIsEditing(!isEditing);
    };
    $[10] = isEditing;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleEditToggle = t8;
  if (isEditing) {
    return <PatientCardEditable patient={patient} onClose={handleEditToggle} compareMode={compareMode} secondaryObject={secondaryPatient} fields={fields} />;
  }
  const t9 = `/patients/${patient.displayId}`;
  let t10;
  if ($[12] !== handleEditToggle || $[13] !== patient.id || $[14] !== t9) {
    t10 = createCardActionsProps({
      id: patient.id,
      label: "Patient ID",
      href: t9,
      customProps: [{
        label: "Edit Patient",
        onClick: handleEditToggle
      }]
    });
    $[12] = handleEditToggle;
    $[13] = patient.id;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const cardActionsProps = t10;
  const T0 = ContextMenu;
  const T1 = ContextMenuTrigger;
  const t11 = true;
  const T2 = Card;
  const t12 = "w-full focus:outline-none focus:ring-0";
  const t13 = 0;
  const T3 = CardHeader;
  const t14 = "flex items-center justify-between";
  let t15;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="flex items-center"><UserCircle className="h-6 w-6 mr-2" /><CardTitle className="text-md font-semibold">Patient Information</CardTitle></div>;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  const t16 = "ml-auto flex items-center gap-2";
  const t17 = patient && renderBadge(compareMode, secondaryPatient, hasAnyConflicts);
  let t18;
  if ($[17] !== pathname || $[18] !== patient) {
    t18 = patient && !pathname?.startsWith(`/patients/${patient.displayId}`) ? <LinkToEntity href={`/patients/${patient.displayId}`} /> : null;
    $[17] = pathname;
    $[18] = patient;
    $[19] = t18;
  } else {
    t18 = $[19];
  }
  let t19;
  if ($[20] !== canContact || $[21] !== canWrite || $[22] !== cardActionsProps || $[23] !== patient || $[24] !== router) {
    t19 = canWrite && patient && <EntityDropdownMenu cardActionsProps={cardActionsProps} customProps={canContact ? [{
      label: "Contact Patient",
      onClick: () => router.push(`/inbox/${patient.displayId}`)
    }] : []} />;
    $[20] = canContact;
    $[21] = canWrite;
    $[22] = cardActionsProps;
    $[23] = patient;
    $[24] = router;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== t17 || $[27] !== t18 || $[28] !== t19) {
    t20 = <div className={t16}>{t17}{t18}{t19}</div>;
    $[26] = t17;
    $[27] = t18;
    $[28] = t19;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== t15 || $[31] !== t20) {
    t21 = <div className={t14}>{t15}{t20}</div>;
    $[30] = t15;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== T3 || $[34] !== t21) {
    t22 = <T3>{t21}</T3>;
    $[33] = T3;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== compareMode || $[37] !== fields || $[38] !== patient || $[39] !== secondaryPatient) {
    t23 = <CardContent>{!patient ? <ErrorAlert message="Patient not found" compact={true} /> : <FieldLayout fields={fields} patient={patient} secondaryObject={secondaryPatient} compareMode={compareMode} />}</CardContent>;
    $[36] = compareMode;
    $[37] = fields;
    $[38] = patient;
    $[39] = secondaryPatient;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== T2 || $[42] !== t22 || $[43] !== t23) {
    t24 = <T2 className={t12} tabIndex={t13}>{t22}{t23}</T2>;
    $[41] = T2;
    $[42] = t22;
    $[43] = t23;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== T1 || $[46] !== t24) {
    t25 = <T1 asChild={t11}>{t24}</T1>;
    $[45] = T1;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== canWrite || $[49] !== cardActionsProps || $[50] !== patient) {
    t26 = canWrite && patient && <CardActions {...cardActionsProps} isContextMenu={true} />;
    $[48] = canWrite;
    $[49] = cardActionsProps;
    $[50] = patient;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] !== T0 || $[53] !== t25 || $[54] !== t26) {
    t27 = <T0>{t25}{t26}</T0>;
    $[52] = T0;
    $[53] = t25;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  return t27;
}
function GridItemSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="inline-block align-text-bottom h-4 w-[100px]" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function PatientCardSkeleton() {
  const $ = _c(11);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CardHeader><div className="flex items-center"><UserCircle className="h-6 w-6 mr-2" /><CardTitle className="text-md font-semibold">Patient Information</CardTitle></div></CardHeader>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="font-semibold">Last Name:</span>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div>{t1} <GridItemSkeleton /></div>;
    t3 = <span className="font-semibold">Given Name:</span>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div>{t3} <GridItemSkeleton /></div>;
    t5 = <span className="font-semibold">Date of Birth:</span>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div>{t5} <GridItemSkeleton /></div>;
    t7 = <span className="font-semibold">Gender:</span>;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div>{t7} <GridItemSkeleton /></div>;
    t9 = <span className="font-semibold">Email:</span>;
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Card className="w-full">{t0}<CardContent><div className="grid grid-cols-3 gap-2 text-sm">{t2}{t4}{t6}{t8}<div>{t9} <GridItemSkeleton /></div></div></CardContent></Card>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  return t10;
}